import { useMemo, useState, type DragEvent, type ReactNode } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Button,
  Modal,
  PlusIcon,
  RotateLeftIcon,
  SearchIcon,
  TrashIcon,
} from "@fawkes/ui";

import "./TegelIndelingDialog.css";

/** Eén tegel zoals de dialoog hem toont. */
export interface TegelKeuze {
  key: string;
  titel: string;
  omschrijving: string;
  icoon: ReactNode;
  /** Kleurfamilie van het icoonvlak (zoals in het ontwerp: elke soort tegel zijn eigen tint). */
  tint: "accent" | "groen" | "oranje" | "paars" | "rood" | "geel" | "grijs" | "turkoois";
  categorie: string;
  /** Vast bovenaan en altijd zichtbaar (Basisinformatie). */
  vast?: boolean;
}

export interface TegelIndeling {
  order: string[];
  hidden: string[];
}

/**
 * "Tegels tonen of verbergen" — links wat je nu niet ziet, rechts wat je wél ziet in de volgorde waarin het
 * op de dierpagina staat. Toevoegen met + (of slepen naar rechts), weghalen met het prullenbakje,
 * verplaatsen met de pijltjes of door te slepen. Pas "Opslaan" bewaart de indeling.
 *
 * Tegels die hier niet in de lijst staan (een module die uit staat) houden hun plek en stand: één keer
 * opslaan mag die niet stilletjes wegpoetsen.
 */
export function TegelIndelingDialog({
  tegels,
  indeling,
  standaardVolgorde,
  categorieen,
  onClose,
  onSave,
  t,
}: {
  tegels: TegelKeuze[];
  indeling: TegelIndeling;
  standaardVolgorde: readonly string[];
  categorieen: { key: string; label: string }[];
  onClose: () => void;
  onSave: (indeling: TegelIndeling) => void;
  t: (key: string) => string;
}) {
  const perKey = useMemo(() => new Map(tegels.map((tg) => [tg.key, tg])), [tegels]);
  const vast = tegels.filter((tg) => tg.vast).map((tg) => tg.key);

  // Rechts: zichtbare tegels in hun huidige volgorde (vaste tegels altijd bovenaan).
  const [zichtbaar, setZichtbaar] = useState<string[]>(() => {
    const inVolgorde = indeling.order.filter((k) => perKey.has(k) && !indeling.hidden.includes(k));
    const nietInVolgorde = tegels.map((tg) => tg.key).filter((k) => !indeling.order.includes(k) && !indeling.hidden.includes(k));
    const alles = [...inVolgorde, ...nietInVolgorde];
    return [...vast, ...alles.filter((k) => !vast.includes(k))];
  });
  const [zoek, setZoek] = useState("");
  const [categorie, setCategorie] = useState("");
  const [sleep, setSleep] = useState<{ key: string; van: "links" | "rechts" } | null>(null);
  const [boven, setBoven] = useState<string | null>(null);

  const beschikbaar = tegels
    .filter((tg) => !zichtbaar.includes(tg.key))
    .filter((tg) => !categorie || tg.categorie === categorie)
    .filter((tg) => {
      const q = zoek.trim().toLowerCase();
      return !q || `${tg.titel} ${tg.omschrijving}`.toLowerCase().includes(q);
    });

  const voegToe = (key: string, voor?: string) =>
    setZichtbaar((z) => {
      const zonder = z.filter((k) => k !== key);
      const plek = voor ? zonder.indexOf(voor) : -1;
      if (plek < 0) return [...zonder, key];
      return [...zonder.slice(0, Math.max(plek, vast.length)), key, ...zonder.slice(Math.max(plek, vast.length))];
    });
  const haalWeg = (key: string) => setZichtbaar((z) => z.filter((k) => k !== key));
  const verschuif = (key: string, stap: -1 | 1) =>
    setZichtbaar((z) => {
      const i = z.indexOf(key);
      const j = i + stap;
      if (i < 0 || j < vast.length || j >= z.length) return z;
      const nieuw = [...z];
      [nieuw[i], nieuw[j]] = [nieuw[j], nieuw[i]];
      return nieuw;
    });
  const standaard = () =>
    setZichtbaar((z) => [
      ...vast,
      ...standaardVolgorde.filter((k) => z.includes(k) && !vast.includes(k)),
      ...z.filter((k) => !standaardVolgorde.includes(k) && !vast.includes(k)),
    ]);

  const opslaan = () => {
    const inDialoog = new Set(tegels.map((tg) => tg.key));
    // Tegels buiten de dialoog houden hun plek; binnen de dialoog volgt de volgorde de rechterlijst,
    // en de verborgen tegels schuiven achteraan (zo staan ze meteen onderaan als je ze weer aanzet).
    const verborgenInDialoog = tegels.map((tg) => tg.key).filter((k) => !zichtbaar.includes(k));
    const buiten = indeling.order.filter((k) => !inDialoog.has(k));
    return onSave({
      order: [...zichtbaar, ...verborgenInDialoog, ...buiten],
      hidden: [...indeling.hidden.filter((k) => !inDialoog.has(k)), ...verborgenInDialoog],
    });
  };

  // ── slepen ────────────────────────────────────────────────────────────────
  const startSleep = (key: string, van: "links" | "rechts") => (e: DragEvent) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
    setSleep({ key, van });
  };
  const eindeSleep = () => {
    setSleep(null);
    setBoven(null);
  };
  const dropOpRij = (doel: string) => (e: DragEvent) => {
    e.preventDefault();
    if (!sleep || sleep.key === doel || perKey.get(sleep.key)?.vast) return eindeSleep();
    voegToe(sleep.key, doel);
    eindeSleep();
  };
  const dropOpRechts = (e: DragEvent) => {
    e.preventDefault();
    if (sleep && !perKey.get(sleep.key)?.vast) voegToe(sleep.key);
    eindeSleep();
  };
  const dropOpLinks = (e: DragEvent) => {
    e.preventDefault();
    if (sleep?.van === "rechts" && !perKey.get(sleep.key)?.vast) haalWeg(sleep.key);
    eindeSleep();
  };

  const icoonVlak = (tg: TegelKeuze) => (
    <span className={`tegel-indeling__icoon tegel-indeling__icoon--${tg.tint}`} aria-hidden="true">
      {tg.icoon}
    </span>
  );

  return (
    <Modal
      open
      size="wide"
      title={t("animalDetailView.tileVisibility.title")}
      onClose={onClose}
      footer={
        <div className="tegel-indeling__voet">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={opslaan}>
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <p className="tegel-indeling__intro">{t("animalDetailView.tileVisibility.intro")}</p>
      <div className="tegel-indeling">
        {/* ── links: niet zichtbaar ── */}
        <section
          className={`tegel-indeling__kolom${sleep?.van === "rechts" ? " is-doel" : ""}`}
          onDragOver={(e) => sleep?.van === "rechts" && e.preventDefault()}
          onDrop={dropOpLinks}
          aria-label={t("animalDetailView.tileVisibility.available")}
        >
          <div className="tegel-indeling__filters">
            <label className="tegel-indeling__zoek">
              <SearchIcon width={16} height={16} aria-hidden="true" />
              <input
                type="search"
                autoComplete="off"
                value={zoek}
                placeholder={t("animalDetailView.tileVisibility.search")}
                aria-label={t("animalDetailView.tileVisibility.search")}
                onChange={(e) => setZoek(e.target.value)}
              />
            </label>
            <select
              className="tegel-indeling__categorie"
              value={categorie}
              aria-label={t("animalDetailView.tileVisibility.category")}
              onChange={(e) => setCategorie(e.target.value)}
            >
              <option value="">{t("animalDetailView.tileVisibility.allCategories")}</option>
              {categorieen.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <h3 className="tegel-indeling__kop">{t("animalDetailView.tileVisibility.available")}</h3>
          <p className="tegel-indeling__sub">{t("animalDetailView.tileVisibility.availableHint")}</p>
          <ul className="tegel-indeling__lijst">
            {beschikbaar.map((tg) => (
              <li
                key={tg.key}
                className="tegel-indeling__rij"
                draggable
                onDragStart={startSleep(tg.key, "links")}
                onDragEnd={eindeSleep}
              >
                {icoonVlak(tg)}
                <span className="tegel-indeling__tekst">
                  <strong>{tg.titel}</strong>
                  <span>{tg.omschrijving}</span>
                </span>
                <button
                  type="button"
                  className="tegel-indeling__plus"
                  aria-label={`${t("animalDetailView.tileVisibility.add")}: ${tg.titel}`}
                  title={t("animalDetailView.tileVisibility.add")}
                  onClick={() => voegToe(tg.key)}
                >
                  <PlusIcon width={16} height={16} />
                </button>
              </li>
            ))}
            {beschikbaar.length === 0 && (
              <li className="tegel-indeling__leeg">
                {zoek || categorie
                  ? t("animalDetailView.tileVisibility.noMatch")
                  : t("animalDetailView.tileVisibility.allVisible")}
              </li>
            )}
          </ul>
        </section>

        {/* ── rechts: zichtbaar, in volgorde ── */}
        <section
          className={`tegel-indeling__kolom tegel-indeling__kolom--zichtbaar${sleep?.van === "links" ? " is-doel" : ""}`}
          onDragOver={(e) => sleep && e.preventDefault()}
          onDrop={dropOpRechts}
          aria-label={t("animalDetailView.tileVisibility.selected")}
        >
          <div className="tegel-indeling__kopregel">
            <div>
              <h3 className="tegel-indeling__kop">{t("animalDetailView.tileVisibility.selected")}</h3>
              <p className="tegel-indeling__sub">{t("animalDetailView.tileVisibility.selectedHint")}</p>
            </div>
            <button type="button" className="tegel-indeling__standaard" onClick={standaard}>
              <RotateLeftIcon width={15} height={15} aria-hidden="true" />
              {t("animalDetailView.tileVisibility.defaultOrder")}
            </button>
          </div>
          <ol className="tegel-indeling__lijst">
            {zichtbaar.map((key, i) => {
              const tg = perKey.get(key);
              if (!tg) return null;
              const eerste = i <= vast.length || tg.vast;
              const laatste = i === zichtbaar.length - 1 || tg.vast;
              return (
                <li
                  key={key}
                  className={`tegel-indeling__rij${boven === key ? " is-boven" : ""}${sleep?.key === key ? " is-sleep" : ""}`}
                  draggable={!tg.vast}
                  onDragStart={tg.vast ? undefined : startSleep(key, "rechts")}
                  onDragEnd={eindeSleep}
                  onDragOver={(e) => {
                    if (!sleep || tg.vast) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setBoven(key);
                  }}
                  onDragLeave={() => setBoven((b) => (b === key ? null : b))}
                  onDrop={(e) => {
                    e.stopPropagation();
                    dropOpRij(key)(e);
                  }}
                >
                  <span className={`tegel-indeling__greep${tg.vast ? " is-vast" : ""}`} aria-hidden="true">
                    ⠿
                  </span>
                  {icoonVlak(tg)}
                  <span className="tegel-indeling__tekst">
                    <strong>{tg.titel}</strong>
                    <span>{tg.omschrijving}</span>
                  </span>
                  <span className="tegel-indeling__acties">
                    <button
                      type="button"
                      className="tegel-indeling__pijl"
                      disabled={eerste}
                      aria-label={`${t("animalDetailView.tileVisibility.up")}: ${tg.titel}`}
                      title={t("animalDetailView.tileVisibility.up")}
                      onClick={() => verschuif(key, -1)}
                    >
                      <ArrowUpIcon width={15} height={15} />
                    </button>
                    <button
                      type="button"
                      className="tegel-indeling__pijl"
                      disabled={laatste}
                      aria-label={`${t("animalDetailView.tileVisibility.down")}: ${tg.titel}`}
                      title={t("animalDetailView.tileVisibility.down")}
                      onClick={() => verschuif(key, 1)}
                    >
                      <ArrowDownIcon width={15} height={15} />
                    </button>
                    <button
                      type="button"
                      className="tegel-indeling__weg"
                      disabled={tg.vast}
                      aria-label={`${t("animalDetailView.tileVisibility.hide")}: ${tg.titel}`}
                      title={tg.vast ? t("animalDetailView.tileVisibility.pinned") : t("animalDetailView.tileVisibility.hide")}
                      onClick={() => haalWeg(key)}
                    >
                      <TrashIcon width={15} height={15} />
                    </button>
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </Modal>
  );
}
